import _ from 'lodash'

/**
 * TMR API Provider
 *
 * Allows modules to add/change/remove endpoints during the configuration
 * phase. Endpoints can be strings or objects with `protocol`, `basePath`,
 * `prefix` and `path` properties.
 *
 * (Note that you can also completely override all endpoints by redeclaring the
 * {@link tmrDefaultApiEndpoints} constant)
 *
 * @example
 * function MyConfig(TmrApiProvider) { 'ngInject';
 *   TmrApiProvider.endpoints.login = {
 *     basePath: 'example.com',
 *     prefix: 'auth.api',
 *     path: 'auth/startSession'
 *   };
 *   TmrApiProvider.endpoints.user = 'some/other/endpoint';
 *   TmrApiProvider.endpoints.someWebsocket = {
 *     protocol: 'ws',
 *     path: 'some/websocket/endpoint'
 *   };
 * }
 *
 * @type {ngProvider}
 */
class TmrApiProvider {
  static ngType = 'provider'

  constructor() {
    this.endpoints = {}
  }

  $get(
    $http,
    tmrApiProtocol,
    tmrApiBasePath,
    tmrApiPrefix,
    tmrApiAuthToken,
    tmrDefaultApiEndpoints,
    DeviceManager
  ) {
    'ngInject'
    // Finalize endpoints filling in with defaults.
    _.defaults(this.endpoints, tmrDefaultApiEndpoints)
    return new TmrApi(
      $http,
      tmrApiProtocol,
      tmrApiBasePath,
      tmrApiPrefix,
      tmrApiAuthToken,
      DeviceManager,
      this.endpoints
    )
  }
}

/**
 * Utilities to interact with the backend using the TMR API
 *
 * @type {ngService}
 */
class TmrApi {
  constructor(
    $http,
    tmrApiProtocol,
    tmrApiBasePath,
    tmrApiPrefix,
    tmrApiAuthToken,
    DeviceManager,
    endpoints
  ) {
    Object.assign(this, {
      $http,
      tmrApiProtocol,
      tmrApiBasePath,
      tmrApiPrefix,
      tmrApiAuthToken,
      DeviceManager,
      endpoints,
    })
  }

  /**
   * Builds an URL to a TMR API Endpoint. Parameters are not replaced.
   *
   * @example
   * TmrApi.endpoint('login') may return http://localhost:8080/tmr/authentication.api/auth/startSession
   *
   * @param {string} name The endpoint name, from {@link tmrDefaultApiEndpoints}
   */
  endpoint(name) {
    let endpoint = this.endpoints[name]
    if (endpoint === undefined) {
      throw new Error(`TMR API Endpoint ${name} does not exist`)
    }

    let protocol
    let basePath
    let apiPrefixPart
    let endpointPart

    if (typeof endpoint === 'object') {
      if (endpoint.deviceManager) {
        this.DeviceManager.processEndpoint(endpoint)
      }
      protocol = endpoint.protocol || this.tmrApiProtocol
      basePath = endpoint.basePath || this.tmrApiBasePath
      apiPrefixPart =
        typeof endpoint.prefix === 'string'
          ? endpoint.prefix
          : this.tmrApiPrefix
      endpointPart = endpoint.path
    } else {
      protocol = this.tmrApiProtocol
      basePath = this.tmrApiBasePath
      apiPrefixPart = this.tmrApiPrefix
      endpointPart = endpoint
    }

    // Support empty apiPrefix ("/")
    if (apiPrefixPart !== '/') {
      apiPrefixPart = `/${apiPrefixPart}/`
    }

    return `${protocol}://${basePath}${apiPrefixPart}${endpointPart}`
  }

  /**
   * Builds an URL with replacements to a TMR API Endpoint.
   *
   * @example
   * TmrApi.url('Antenna', { id: 123 })
   *
   * @param {string} endpoint The endpoint name, from {@link tmrDefaultApiEndpoints}
   * @param {object} replacements A map of $http placeholders to be replaced in the url
   */
  url(endpoint, replacements) {
    let url = this.endpoint(endpoint)
    for (let param in replacements) {
      // @todo Breaks in many situations.
      // If param is empty, remove the url part entirely.
      url = replacements[param]
        ? url.replace(`:${param}`, replacements[param])
        : url.replace(`/:${param}`, '')
    }
    return url
  }

  /**
   * Performs a GET request to a TMR API Endpoint.
   *
   * @example
   * TmrApi.get('Antenna', { id: 123 })
   *
   * @param {string} endpoint The endpoint name, from {@link tmrDefaultApiEndpoints}
   * @param {object} replacements A map of $http placeholders to be replaced in the url
   * @param {object} config $http configuration options
   */
  get(endpoint, replacements, config = {}) {
    config.headers = config.headers ?? {}
    config.headers[this.tmrApiAuthToken] = null // Setup token for TmrApiAuthInterceptor
    return this.$http.get(this.url(endpoint, replacements), config)
  }

  /**
   * Performs a POST request to a TMR API Endpoint.
   *
   * @example
   * TmrApi.post('Place.transferDetail', { id: 123 }, somePostData)
   *
   * @param {string} endpoint The endpoint name, from {@link tmrDefaultApiEndpoints}
   * @param {object} replacements A map of $http placeholders to be replaced in the url
   * @param {object} data The POST body
   * @param {object} config $http configuration options
   */
  post(endpoint, replacements, data, config = {}) {
    config.headers = config.headers ?? {}
    config.headers[this.tmrApiAuthToken] = null // Setup token for TmrApiAuthInterceptor
    return this.$http.post(this.url(endpoint, replacements), data, config)
  }
}

export { TmrApiProvider as TmrApi }
